/* eslint-disable max-classes-per-file */

'use strict';

define('vb/private/events/eventRegistry',[
  'vb/private/constants',
  'vb/private/log',
  'vb/private/utils',
  'vb/private/stateManagement/stateUtils',
  'vb/private/events/baseEventModel',
  'vb/private/events/eventModel',
  'vb/private/events/undeclaredEventBehavior',
], (Constants, Log, Utils, StateUtils, BaseEventModel, EventModel, UndeclaredEventBehavior) => {
  /**
   * @typedef {import('vb/private/stateManagement/container').default} Container
   * @typedef {import('vb/private/stateManagement/page').default} Page
   */

  const logger = Log.getLogger('/vb/private/events/eventRegistry');

  /**
   * Determines the section in which to store events based on the given behavior.
   *
   * This method checks the provided behavior and returns the corresponding section identifier.
   * If the behavior is `Constants.EventBehaviors.DYNAMIC_COMP`, it returns the behavior itself.
   * Otherwise, it returns the string 'vb'.
   *
   * @param {String} behavior - The behavior type of the event (e.g.,
   * 'notify', 'notifyAndWait', 'checkForCancel', 'transform' (deprecated), 'transformPayload', 'dynamicComponent').
   * @returns {String} - The section identifier where events of the specified behavior should be stored.
   */
  function getSection(behavior) {
    return (behavior === Constants.EventBehaviors.DYNAMIC_COMP) ? behavior : 'vb';
  }

  /**
   * Determines which class should be used for the event model based on the specified behavior.
   *
   * This method checks the provided `behavior` and returns the corresponding class for the event model.
   * If the behavior is `DYNAMIC_COMP`, it returns the `BaseEventModel` class.
   * Otherwise, it defaults to the `EventModel` class.
   *
   * @param {String} behavior - The behavior type of the event (e.g.,
   * 'notify', 'notifyAndWait', 'checkForCancel', 'transform' (deprecated), 'transformPayload', 'dynamicComponent').
   * @returns {typeof BaseEventModel|typeof EventModel} - The class that should be used for the event model.
   * It could be `BaseEventModel` or `EventModel`.
   */
  function getRegistrationClass(behavior) {
    return (behavior === Constants.EventBehaviors.DYNAMIC_COMP) ? BaseEventModel : EventModel;
  }

  /**
   * Returns the fallback class for the given behavior.
   * If the behavior is 'DYNAMIC_COMP', it returns null indicating no default class.
   * For any other behavior, it returns the 'EventModel' class as the fallback.
   *
   * @param {String} behavior - The behavior type of the event (e.g.,
   * 'notify', 'notifyAndWait', 'checkForCancel', 'transform' (deprecated), 'transformPayload', 'dynamicComponent').
   * @returns {typeof EventModel|null} - The fallback class for the specified behavior,
   * or null if there is no default class.
   */
  function getFallbackClass(behavior) {
    return (behavior === Constants.EventBehaviors.DYNAMIC_COMP) ? null : EventModel;
  }

  /**
   * Class representing the Event Registry.
   *
   * All declared Events *must* be registered with this singleton, by the container in which the Event is declared.
   * Components will use the registry to get an EventModel/EventBehavior for all events.
   * The EventRegistry always returns an EventModel for any fired event, whether one has been registered or not;
   * the model abstracts whether an event was declared, or not.
   *
   * The mapping is: {
   *   [container:fullPath + event simple name] : EventModel
   * }
   */
  class EventRegistry {
    /**
     * Creates an instance of EventRegistry.
     *
     * Initializes a two-dimensional map, `registeredMap`, which stores registered events.
     * The map is structured based on sections and event names.
     */
    constructor() {
      // this will be a two-dimensional map of [section][name]
      this.registeredMap = {};
    }

    /**
     * Registers an event for a specified container in the EventRegistry.
     *
     * @param {Container} container - The container that declares the event.
     * @param {String} eventName - The unqualified name of the event; the 'simple' name, with no namespace or prefix.
     * @param {Object} declaration - The JSON declaration for the event.
     * @param {boolean} [isInterface=false] - Indicates if the event is defined in the "interface" section.
     * @returns {null|Function} A function to unregister the event when the container is disposed,
     * or null in case of an error.
     */
    register(container, eventName, declaration, isInterface = false) {
      if (eventName) {
        const decl = declaration || {};

        if (decl.payloadType) {
          if (Utils.isInstanceType(decl.payloadType)) {
            // don't allow 'vb/ServiceProvider', etc.
            logger
              .error(`Declared events do not allow builtin types, skipping: ${eventName}, type: ${decl.payloadType}`);
            return null;
          }
          try {
            // eventName in this call is not used, except in the Exception message
            StateUtils.getType(eventName, { type: decl.payloadType });
          } catch (e) {
            logger.error(`Declared events error, skipping: ${e}`);
            return null;
          }
        }

        // qualify the name with the (extensionId, if any, and) container path
        const extId = container.extensionId || ''; // @todo: does this need to be hierarchical for ext-of-ext?
        const key = EventRegistry.key(extId, container.fullPath, eventName);

        const section = getSection(decl.behavior);
        this.registeredMap[section] = this.registeredMap[section] || {};

        const EventClazz = getRegistrationClass(decl.behavior);

        this.registeredMap[section][key] = new EventClazz(eventName, this, decl, container, isInterface);
        // return a function to be used to unregister the event when the container is disposed
        return () => delete this.registeredMap[section][key];
      }

      return null;
    }

    /**
     * Called during event propagation, returns an EventModel for the requested event.
     * Either find a registered event model, or create an event model for an undeclared event.
     *
     * If the 'name' is passed unqualified, qualify it using the firing container's namespace.
     *
     * If there is a declared event with the qualified name:
     *  - Returns the model if it is in the requesting container's scope (flow cannot access page events, for example)
     *  - Returns null otherwise
     *
     * Otherwise, when there is no declaration found:
     * - If the name is unqualified, return a model for an 'undeclared' event
     * - Return null if the name is qualified
     *
     * The (qualified) event name tells us which container 'type' must have registered the event.
     * If the registration is found, the registrar container's scopeResolver tells us which containers have access.
     *
     * Note that a return of null alone is not enough to determine if an event can be acted on by a container;
     * the event processing must also call listenableBy().
     *
     * Examples:
     *  Flow declares "foo", Page fires "foo:foo", Flow calls get for the event:
     *    EventRegistry.get returns the registered EventModel
     *    Event is propagated from leaf page.
     *
     *  Page declares "foo", Flow fires "page:foo"
     *    EventRegistry.get returns null, because even though it is declared, it is inaccessible to Flow.
     *    No event propagated.
     *
     *  Page declares "foo", Flow fires "foo"
     *    EventRegistry.get returns EventModel with an UndeclaredEventBehavior, because Flow has not used a qualified
     *    name, so we must assume it's an 'undeclared' event.
     *    Event is propagated from firing Flow (undeclared behavior) - only listeners for 'foo' are called.
     *
     * @todo: more work could be done to simplify differences between inaccessible declared and undeclared events.
     *
     * @param {Container|Page} container - The container asking for the Event definition, for handling.
     * @param {Container|Page} containerThatFired - The container that fired the event.
     * @param {String} name - Optionally namespaced, according to Event namespacing rules
     * (e.g., "page:foo", "base/flow:bar").
     * @param {String} [behavior] - The behavior of the event. (e.g.,
     * 'notify', 'notifyAndWait', 'checkForCancel', 'transform' (deprecated), 'transformPayload', 'dynamicComponent').
     * @returns {null|EventModel} null means the event is not accessible by the requesting container.
     */
    get(container, containerThatFired, name, behavior) {
      // split the name in to <ext|'base'>/<container>:<name>, providing defaults where needed
      const nameParts = EventModel.parseName(containerThatFired, name);
      const decl = EventRegistry.findEventDecl(containerThatFired, this.registeredMap, nameParts, behavior);

      // accessibleBy checks if the Page or Flow that is asking for it is the Page or Flow that
      // is in the scope of the container that declared the event
      if (decl) {
        const actualNameParts = EventModel.parseName(decl.container, decl.fullName);

        // if we are looking for the  'base' event in v2 structure, extension id of the
        // found event will not match the extension id of the provided event name, so make
        // sure to use the proper event name
        if (nameParts.ext !== actualNameParts.ext) {
          return decl.accessibleBy(container, decl.fullName) ? decl : null;
        }

        return decl.accessibleBy(container, name) ? decl : null;
      }

      // we didn't find a declared event, so return an 'undeclared' if it's a plain name (no prefix, etc.),
      // or null if it's qualified, as if referencing a declared event ('page: someFoo')
      if (UndeclaredEventBehavior.isNamespaced(name)) {
        return null;
      }

      const FallbackClazz = getFallbackClass(behavior);
      return FallbackClazz ? new FallbackClazz(name, this) : null;
    }

    /**
     * Traverses the extension hierarchy and searches for the event declaration based
     * on the given event name, firing container, and event behavior.
     *
     * @param {Container|Page} container - The container that fired the event.
     * @param {Object} registeredMap - A map of registered events.
     * @param {Object} nameParts - Parsed event name containing extension and prefix information.
     * @param {String} behavior - The event behavior. (e.g.,
     * 'notify', 'notifyAndWait', 'checkForCancel', 'transform' (deprecated), 'transformPayload', 'dynamicComponent').
     * @returns {null|EventModel} - Returns null if no event declaration was found,
     * or the EventModel if the declaration is found.
     */
    static findEventDecl(container, registeredMap, nameParts, behavior) {
      if (!container) {
        return null;
      }

      let containerForPrefix = container;
      let extId = nameParts.ext;

      // if we are searching for the base event, look for the
      // base container and use its extension id for searching for the event
      if (nameParts.prefix !== Constants.GLOBAL_PREFIX && nameParts.ext === Constants.ExtensionNamespaces.BASE) {
        containerForPrefix = EventModel.baseContainer(container);
        extId = containerForPrefix.extensionId;
      }

      const scopeResolver = containerForPrefix.scopeResolver || {}; // shouldn't need the fallback

      if (scopeResolver && scopeResolver[nameParts.prefix]) {
        // events in registry are always cached with the extension id, so make sure to use
        // the extension id of the extension where we are looking up the event
        // and NOT nameParts.ext because it may be set to 'base' so the lookup will fail in this case
        const key = EventRegistry.key(extId, scopeResolver[nameParts.prefix].fullPath, nameParts.name);

        const section = getSection(behavior);

        const decl = registeredMap[section] && registeredMap[section][key];
        if (decl) {
          return decl;
        }

        // check if we are looking for the 'base' event and haven't found the declaration yet,
        // if so, continue traversing the extension hierarchy
        if (nameParts.ext === Constants.ExtensionNamespaces.BASE) {
          return EventRegistry.findEventDecl(containerForPrefix.base, registeredMap, nameParts, behavior);
        }
      }

      return null;
    }

    /**
     * Convenience method to match the name with the listener declaration in the container.
     *
     * @param {Container} container - The container that may contain the listener declaration.
     * @param {String} eventName - The name of the event to match with the listener.
     * @param {EventModel} eventModel - The event model associated with the event.
     * @returns {Object|null} - Returns the listener definition object if a match is found,
     * or null if no match is found.
     */
    // eslint-disable-next-line class-methods-use-this
    findListener(container, eventName, eventModel) {
      const listenerDefs = (container.definition && container.definition.eventListeners) || {};
      if (!listenerDefs) {
        return null;
      }

      // if the auto wired listener name is provided, we need to check
      // if the listener is defined in the given container and whether it's enabled for auto-wiring
      const autoWiredListenerName = eventModel && eventModel.autoWire;
      if (autoWiredListenerName) {
        const listenerDef = listenerDefs[autoWiredListenerName];
        if (listenerDef) {
          // if auto wiring is set to selfOnly, make sure it's limited only to the parent container
          if (listenerDef.autoWire === Constants.EventAutoWireMode.FULL
            || (listenerDef.autoWire === Constants.EventAutoWireMode.SELF_ONLY
                && eventModel.container.parent === container)) {
            return listenerDef;
          }
        }
      }

      // if the whole name is there, return it
      if (listenerDefs[eventName]) {
        return listenerDefs[eventName];
      }

      // split the name
      const parts = EventModel.parseName(container, eventName);

      // check if event and eventListener are in the same extension
      // or if it's a global event since global events could be accessed from the App UIs that
      // don't extend the host app or in a different extension, but should still have access to it
      if (parts.ext === container.extensionId || parts.prefix === Constants.GLOBAL_PREFIX) {
        const shortName = `${parts.prefix}:${parts.name}`;
        if (listenerDefs[shortName]) {
          return listenerDefs[shortName];
        }
      }

      // if the prefix matches our container class, look for the 'simple' name, without the prefix
      if (parts.ext === Constants.ExtensionNamespaces.BASE
          || EventRegistry.isInExtensionHierarchy(container, parts.ext)) {
        // check for abbreviated name, without the 'base' namespace (ex: /flow:foo)
        // only add the leading slash if we are an extension container
        const shortName = `${container.isExtension() ? '/' : ''}${parts.prefix}:${parts.name}`;
        if (listenerDefs[shortName]) {
          return listenerDefs[shortName];
        }

        const fullName = `${Constants.ExtensionNamespaces.BASE}/${parts.prefix}:${parts.name}`;
        if (listenerDefs[fullName]) {
          return listenerDefs[fullName];
        }
      }

      // if we are in the same container scope, match the simple name
      if (parts.prefix === EventModel.basePrefix(container) && listenerDefs[parts.name]) {
        return listenerDefs[parts.name];
      }

      // if the container is in the host application and this is a custom global event
      // and no listener was found so far, for the backward compatibility, check
      // for no scope and/or the application scope listeners for this event instead
      if (!container.package && parts.prefix === Constants.GLOBAL_PREFIX && EventRegistry.isCustomEvent(parts.name)) {
        return !container.isExtension() && listenerDefs[parts.name]
          ? listenerDefs[parts.name]
          : this.findListener(container,
            `${parts.ext}/${Constants.APPLICATION_PREFIX}:${parts.name}`,
            eventModel);
      }

      // just return the raw name, if it exists
      return listenerDefs[eventName];
    }

    /**
     * Determines whether the given event is a custom event.
     *
     * Custom events are events that do not start with a predefined event marker or are not listed
     * in the predefined set of VB events as specified by `Constants.VB_EVENTS`.
     *
     * @param {String} eventName - The name of the event to check.
     * @returns {boolean} Returns `true` if the event is a custom event; otherwise, returns `false`.
     */
    static isCustomEvent(eventName) {
      return !eventName.startsWith(Constants.VB_EVENT_MARKER) || Constants.VB_EVENTS.indexOf(eventName) < 0;
    }

    /**
     * Traverses through the extensions hierarchy of a given container to determine whether
     * the given extension id is present in the hierarchy.
     *
     * @param {Container} container - The current container to start the traversal from.
     * @param {String} eventExtensionId - The extension id to search for within the hierarchy.
     * @returns {boolean} - Returns `true` if the extension id is found in the extension hierarchy; `false` otherwise.
     */
    static isInExtensionHierarchy(container, eventExtensionId) {
      let currentContainer = container;
      if (currentContainer && currentContainer.base) {
        currentContainer = currentContainer.base;
        if (currentContainer.extensionId === eventExtensionId) {
          return true;
        }

        return EventRegistry.isInExtensionHierarchy(currentContainer, eventExtensionId);
      }

      return false;
    }

    /**
     * Generates a unique key for an event within the registry.
     *
     * This method constructs a unique key for the event by combining the provided extension ID,
     * the container's full path, and the event's simple name. This key is different from the
     * event's qualified name as it uses the full path of the container instead of the container prefix
     * (e.g., 'application:', 'flow:', etc.).
     *
     * @param {String} extId - The ID of the extension that contains the event.
     * @param {String} containerPath - The full path of the container where the event is declared.
     * @param {String} name - The unqualified (simple) name of the event.
     * @returns {String} A unique key combining the extension ID, container path, and event name.
     */
    static key(extId, containerPath, name) {
      const path = containerPath || ''; // only used for generating a unique key
      return `${extId || Constants.ExtensionNamespaces.BASE}/${path.toLowerCase()}:${name}`;
    }
  }

  return new EventRegistry(); // singleton
});

